import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, Flame, Loader2, CreditCard, MessageSquare } from "lucide-react";
import { PRICING } from "@/constants/site";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOOKUPS = {
  "carte-custom": "pokebastos_carte_custom",
  "mise-en-valeur": "pokebastos_mise_en_valeur",
  "tableau-catalogue": "pokebastos_tableau_catalogue",
  "tableau-sur-mesure": "pokebastos_tableau_sur_mesure",
};

export default function Pricing() {
  const [paying, setPaying] = useState(null);

  const handlePay = async (plan) => {
    if (paying) return;
    setPaying(plan.id);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: LOOKUPS[plan.id],
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error("Impossible de démarrer le paiement — réessayez ou passez par le formulaire.");
      setPaying(null);
    }
  };

  return (
    <section id="tarifs" className="relative py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-12 flex flex-col gap-6 md:mb-16 md:flex-row md:items-end md:justify-between"
        >
          <div>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-amber-300">04 — Tarifs & devis</p>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Des formules <span className="text-holo">sur mesure</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-[#a09bb5] md:text-right">
            Tarifs fixes et transparents — paiement sécurisé par carte bancaire, devis gratuit pour
            toute demande particulière.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PRICING.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 34 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: EASE, delay: i * 0.1 }}
              data-testid={`pricing-card-${plan.id}`}
              className={`relative flex flex-col rounded-3xl p-8 transition-transform duration-500 hover:-translate-y-1.5 ${
                plan.popular
                  ? "holo-border shadow-[0_0_60px_rgba(255,0,127,0.12)]"
                  : "border border-white/10 bg-[#100e19]"
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-3.5 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-amber-400 px-4 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-[#08070d]">
                  <Flame size={11} /> Populaire
                </span>
              )}
              <h3 className="font-display text-xl font-bold text-white">{plan.name}</h3>
              <p className="mt-2 text-sm text-[#a09bb5]">{plan.desc}</p>
              <p className="mt-6 flex items-baseline gap-1.5">
                <span className={`font-display text-4xl font-extrabold ${plan.popular ? "text-holo" : "text-white"}`}>
                  {plan.price} €
                </span>
              </p>
              <ul className="mt-7 flex-1 space-y-3.5">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-[#c8c4d8]">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-cyan-400/30 bg-cyan-400/10">
                      <Check size={11} className="text-cyan-300" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                data-testid={`pricing-pay-${plan.id}`}
                onClick={() => handlePay(plan)}
                disabled={paying !== null}
                className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-all duration-300 disabled:opacity-60 ${
                  plan.popular ? "btn-holo text-[#08070d]" : "btn-holo text-[#08070d]"
                }`}
              >
                {paying === plan.id ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Redirection…
                  </>
                ) : (
                  <>
                    <CreditCard size={16} /> Payer en ligne — {plan.price} €
                  </>
                )}
              </button>
              <button
                type="button"
                data-testid={`pricing-devis-${plan.id}`}
                onClick={() => scrollToSection("contact")}
                className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-[#a09bb5] transition-colors hover:text-white"
              >
                <MessageSquare size={12} />
                ou demander un devis d'abord
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
